/**
 * Hero Section Skeleton
 * Displays a subtle loading placeholder while Hero content initializes
 * Reduces perceived load time and prevents user refresh behavior
 */

const HeroSkeleton = () => {
  return (
    <section className="relative min-h-[85vh] md:min-h-[75vh] flex items-center pt-24 md:pt-20 overflow-hidden">
      {/* Background gradient placeholder */}
      <div className="absolute inset-0 bg-gradient-sky" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Content skeleton */}
          <div className="text-center lg:text-left animate-pulse">
            {/* Google Reviews Badge skeleton */}
            <div className="flex justify-center lg:justify-start mb-6">
              <div className="h-8 w-48 bg-muted/40 rounded-full" />
            </div>

            {/* Headline skeleton */}
            <div className="space-y-3 mb-6">
              <div className="h-10 md:h-14 bg-muted/40 rounded-lg w-full max-w-lg mx-auto lg:mx-0" />
              <div className="h-10 md:h-14 bg-muted/40 rounded-lg w-3/4 max-w-md mx-auto lg:mx-0" />
            </div>

            {/* Subheadline skeleton */}
            <div className="space-y-2 mb-8 max-w-lg mx-auto lg:mx-0">
              <div className="h-5 bg-muted/30 rounded w-full" />
              <div className="h-5 bg-muted/30 rounded w-5/6" />
            </div>

            {/* Benefits skeleton */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-4 mb-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-5 h-5 bg-secondary/30 rounded-full" />
                  <div className="h-5 w-28 bg-muted/30 rounded" />
                </div>
              ))}
            </div>

            {/* Form card skeleton */}
            <div className="bg-card/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-border max-w-md mx-auto lg:mx-0">
              <div className="space-y-3 mb-4">
                <div className="h-6 bg-muted/40 rounded w-40 mx-auto lg:mx-0" />
                <div className="h-4 bg-muted/30 rounded w-56 mx-auto lg:mx-0" />
              </div>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="h-10 bg-muted/30 rounded flex-1" />
                  <div className="h-10 bg-muted/30 rounded flex-1" />
                </div>
                <div className="h-12 bg-gradient-to-r from-accent/40 to-secondary/40 rounded-lg" />
                <div className="h-3 bg-muted/20 rounded w-48 mx-auto" />
              </div>
            </div>
          </div>

          {/* Hero Image skeleton - Desktop only */}
          <div className="hidden lg:block">
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl border border-border bg-muted/30 aspect-[3/2] animate-pulse" />
              
              {/* Floating Stats Card skeleton */}
              <div className="absolute -top-6 -right-6 bg-card rounded-xl p-4 shadow-xl border border-border">
                <div className="h-8 w-16 bg-secondary/30 rounded mb-1" />
                <div className="h-4 w-24 bg-muted/30 rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSkeleton;
